import { useMemo } from "react"
import { usePlan } from "@/shared/hooks/usePlan"
import { GallerySection } from "@/shared/types/product"

interface UseCreditProgressResult {
  creditsRemaining: number
  creditLimit: number
  creditsToBeUsed: number
  creditsAfterUse: number
  isLoading: boolean
}

/**
 * Computes real-time credit progress for the current user.
 * Combines the user's credit balance (from usePlan) with the number of prompts
 * selected across all gallery sections to show pending credit usage.
 */
const useCreditProgress = (sections: GallerySection[]): UseCreditProgressResult => {
  const { creditsRemaining, creditsUsed, isLoading } = usePlan()

  return useMemo(() => {
    const creditsToBeUsed = sections.reduce((sum, s) => sum + s.selectedPromptIds.length, 0)
    const creditLimit = creditsRemaining + creditsUsed
    const creditsAfterUse = Math.max(0, creditsRemaining - creditsToBeUsed)
    return { creditsRemaining, creditLimit, creditsToBeUsed, creditsAfterUse, isLoading }
  }, [sections, creditsRemaining, creditsUsed, isLoading])
}

export { useCreditProgress }
export type { UseCreditProgressResult }
